import { Form, Formik } from "formik";
import { useState } from "react";
import ModalDialog, {
  ModalDialogBody,
  ModalDialogFooter,
} from "../UI/ModalDialog";
// @ts-expect-error pure js
import TextInput from "../UI/TextInput";
import FormikSubmitButton from "../UI/FormikSubmitButton";
import AddressFields from "../UI/AddressFields";
import Callout from "../UI/Callout";
// @ts-expect-error pure js
import { ReactSelectStandalone } from "../UI/SelectStandalone";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
// @ts-expect-error pure js
import { toInitialValuesFromSchema } from "../../models/utils";
// @ts-expect-error pure js
import { ADDRESS_INITIAL_VALUES, addressSchema } from "../../models/address";
// @ts-expect-error pure js
import useProfileBailleurs from "../../hooks/use-profile-bailleurs";
// @ts-expect-error pure js
import { getProfileDisplayName } from "../../utils/profiles";
import type { Profile, ProfileBailleur } from "../Profile/types";
import styles from "./AddressForm.module.css";

export interface AddressFormValues {
  nom: string;
  adresse: string;
  complement_adresse: string;
  cp: string;
  ville: string;
  telephone: string;
}

interface AddressFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  account: Partial<AddressFormValues>;
  onSubmit: (values: AddressFormValues) => Promise<void>;
}

/** Première identité du profil (société ou personne) : c'est elle qui porte l'adresse. */
function billingIdentityOf(profileBailleur: ProfileBailleur) {
  return profileBailleur.profiles?.[0];
}

function billingValuesFromProfile(
  profileBailleur: ProfileBailleur,
  identity: Profile,
  current: AddressFormValues,
): AddressFormValues {
  return {
    nom: getProfileDisplayName(identity) || profileBailleur.nom_profile,
    adresse: identity.adresse ?? "",
    complement_adresse: identity.complement_adresse ?? "",
    cp: identity.cp ?? "",
    ville: identity.ville ?? "",
    telephone: identity.tel || current.telephone,
  };
}

export default function AddressForm({
  open,
  onOpenChange,
  account,
  onSubmit,
}: AddressFormProps) {
  const [error, setError] = useState("");
  const { profileBailleurs } = useProfileBailleurs() as {
    profileBailleurs: ProfileBailleur[];
  };
  const profilesWithAddress = profileBailleurs.filter(
    (profileBailleur) => billingIdentityOf(profileBailleur)?.adresse,
  );

  async function submitHandler(values: AddressFormValues) {
    setError("");
    try {
      await onSubmit(values);
    } catch (err) {
      setError(handleAPIError(err));
    }
  }

  const initialValues: AddressFormValues = toInitialValuesFromSchema(
    addressSchema,
    account,
    ADDRESS_INITIAL_VALUES,
  );

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      validationSchema={addressSchema}
      onSubmit={submitHandler}
    >
      {({ resetForm, setValues, values }) => (
        <ModalDialog
          size="sm"
          open={open}
          header="Adresse de facturation"
          onOpenChange={(nextOpen) => {
            if (!nextOpen) {
              resetForm();
              setError("");
            }
            onOpenChange(nextOpen);
          }}
        >
          <Form>
            <ModalDialogBody className={styles.form}>
              {profilesWithAddress.length > 0 && (
                <ReactSelectStandalone
                  label="Reprendre les coordonnées d'un profil bailleur"
                  options={profilesWithAddress}
                  value={null}
                  getOptionValue={(option: ProfileBailleur) =>
                    String(option.id)
                  }
                  getOptionLabel={(option: ProfileBailleur) =>
                    option.nom_profile
                  }
                  onChange={(option: ProfileBailleur | null) => {
                    const identity = option && billingIdentityOf(option);
                    if (option && identity) {
                      setValues(
                        billingValuesFromProfile(option, identity, values),
                      );
                    }
                  }}
                  placeholder="Choisir un profil bailleur"
                  menuPortalTarget={document.body}
                  isClearable={false}
                />
              )}
              <TextInput
                name="nom"
                label="Prénom / nom / société"
                placeholder="SCI de la colline"
                marginForm=""
              />
              <AddressFields />
              <TextInput
                type="text"
                name="telephone"
                placeholder="0601323123"
                label="Numéro de téléphone"
                marginForm=""
              />
              <Callout variant="danger">{error}</Callout>
            </ModalDialogBody>
            <ModalDialogFooter>
              <FormikSubmitButton>Valider</FormikSubmitButton>
            </ModalDialogFooter>
          </Form>
        </ModalDialog>
      )}
    </Formik>
  );
}
